import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cpu } from 'lucide-react';
import { apiClient, ChatEffort, ChatProvider, ChatSettings } from '../lib/api';
import ConfirmationModal from './ConfirmationModal';
import Toast from './Toast';

const PROVIDER_LABELS: Record<ChatProvider, string> = {
  azure_openai: 'GPT (Azure OpenAI)',
  azure_anthropic: 'Claude Haiku 5.5 (Azure Anthropic)',
};

const EFFORT_OPTIONS: { value: ChatEffort; label: string }[] = [
  { value: 'low', label: 'Low: fastest replies, least reasoning' },
  { value: 'medium', label: 'Medium: balanced (recommended)' },
  { value: 'high', label: 'High: most careful, slower replies' },
];

const deploymentName = (settings: ChatSettings, provider: ChatProvider) =>
  provider === 'azure_openai' ? settings.gpt_deployment || 'not set' : settings.claude_model;

const ChatModelSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: settings, isLoading, error } = useQuery<ChatSettings, Error>({
    queryKey: ['chat-settings'],
    queryFn: apiClient.getChatSettings,
  });
  const [provider, setProvider] = useState<ChatProvider>('azure_openai');
  const [effort, setEffort] = useState<ChatEffort>('medium');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (settings) {
      setProvider(settings.provider);
      setEffort(settings.effort);
    }
  }, [settings]);

  const saveMutation = useMutation<ChatSettings, Error>({
    mutationFn: () => apiClient.updateChatSettings({ provider, effort }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['chat-settings'], saved);
      setToast({ message: 'AI chat model updated. Every server picks it up within 30 seconds.', type: 'success' });
    },
    onError: (err) => setToast({ message: err.message || 'Failed to update the AI chat model', type: 'error' }),
    onSettled: () => setConfirmOpen(false),
  });

  const hasChanges = !!settings && (provider !== settings.provider || effort !== settings.effort);

  return (
    <div className="bg-white shadow rounded-lg mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          <Cpu className="h-5 w-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">AI Chat Model</h3>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          The model behind every tenant's widget chat, expert chat and admin AI assistant.
        </p>
      </div>

      <div className="px-6 py-4">
        {isLoading ? (
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
        ) : error || !settings ? (
          <p className="text-sm text-red-600">Unable to load the AI chat model{error ? `: ${error.message}` : ''}</p>
        ) : (
          <>
            <fieldset>
              <legend className="text-sm font-medium text-gray-700">Provider</legend>
              <div className="mt-2 space-y-2">
                {(Object.keys(PROVIDER_LABELS) as ChatProvider[]).map((value) => {
                  const configured = settings.configured_providers.includes(value);
                  return (
                    <label
                      key={value}
                      className={`flex items-start p-3 border rounded-lg ${
                        provider === value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                      } ${configured ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                    >
                      <input
                        type="radio"
                        name="chat-provider"
                        value={value}
                        checked={provider === value}
                        disabled={!configured || saveMutation.isLoading}
                        onChange={() => setProvider(value)}
                        className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                      />
                      <span className="ml-3">
                        <span className="block text-sm font-medium text-gray-900">{PROVIDER_LABELS[value]}</span>
                        <span className="block text-sm text-gray-500">
                          {configured ? `Deployment: ${deploymentName(settings, value)}` : 'Not configured on this server'}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="mt-4">
              <label htmlFor="chat-effort" className="block text-sm font-medium text-gray-700">
                Reasoning effort
              </label>
              <select
                id="chat-effort"
                value={effort}
                onChange={(e) => setEffort(e.target.value as ChatEffort)}
                disabled={provider !== 'azure_anthropic' || saveMutation.isLoading}
                className="mt-1 block w-full max-w-md rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
              >
                {EFFORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                How much Claude reasons before it answers. Higher effort follows tenant instructions more closely
                but replies more slowly. GPT ignores this setting.
              </p>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {settings.provider_source === 'admin' && settings.updated_by
                  ? `Last changed by ${settings.updated_by}${
                      settings.updated_at ? ` on ${new Date(settings.updated_at).toLocaleString()}` : ''
                    }`
                  : 'Not set in Lawmabase yet: using the CHAT_PROVIDER and CHAT_EFFORT app settings.'}
              </p>
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={!hasChanges || saveMutation.isLoading}
                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {saveMutation.isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => saveMutation.mutate()}
        title="Change the AI chat model?"
        message={`Live chat for every tenant will switch to ${PROVIDER_LABELS[provider]}${
          provider === 'azure_anthropic' ? ` at ${effort} effort` : ''
        } within 30 seconds.`}
        confirmText="Switch model"
        type="warning"
        isLoading={saveMutation.isLoading}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default ChatModelSettings;
